import { useState, useEffect, useContext } from 'react';
import { useClient } from '../../../context/ClientContext';
import { useLocation, useNavigate } from 'react-router-dom';
import HelloHealthStepsConfig from '../../../clients/HelloHealth/stepsConfig';
import configB from '../../../clients/clientB/stepsConfig';
import ClientQuestionBase from '../common/ClientQuestionBase';
import { Box, Typography } from '@mui/material';
import { useClientApi } from '../../../api/client';
import { CLIENT_TYPES } from '../../../api/clientconfig';
import AuthContext from '../../../context/AuthContext';
import { SimpleLoader } from '../../../components/loading/simpleLoader';
import { convertFeetAndInchesToCentimeters, convertPoundsToKilograms } from '../../../utils/conversions';
import ClientButton from '../common/Clientbutton';
import { identifyClientType } from '../../../utils/identifyClientType';

export default function MasterDemographicsPage() {

  const [ clientType, setClientType ] = useState( CLIENT_TYPES.DEFAULT );

  useEffect( () => {
    setClientType( identifyClientType() );
  }, [] );

  const { clientId, landing, theme } = useClient();
  const steps = clientId === 'hellohealth' ? HelloHealthStepsConfig : configB;
  const [ stepIndex, setStepIndex ] = useState( 0 );
  const [ isLoading, setIsLoading ] = useState( false );
  const [ error, setError ] = useState( null );
  const clientApi = useClientApi( clientType );
  const { setOrgUser } = useContext( AuthContext );
  const navigate = useNavigate();

  const defaultUserData = {
    gender: "",
    genderId: 1,
    age: "",
    weight: "",
    height: { cm: "", ft: "", in: "" },
    ethnicity: null,
    regularBp: "",
    isFrontendHeightImperialUnits: true,
    isFrontendWeightImperialUnits: true,
    heightUnit: "in",
    weightUnit: "lbs",
    diabetes: null,
    isPregnant: null,
    isSmoke: null,
    isHighBP: null,
    hasHadHeartAttack: null,
    hasHadStroke: null,
    bpMeds: null,
    isHealthSymptoms: '',
    wellnessGoal: [],
    medicalCondition: [],
    allergies: [],
    isTakingSupplements: null,
    supplements: [],
    email: '',
    name: '',
    isPregnantId: '',
    dateOfBirth: null,
    bloodPressure: ""
  };

  const [ userData, setUserData ] = useState( () => {
    const savedData = localStorage.getItem( `${ clientId }_userData` );
    if ( savedData ) {
      return JSON.parse( savedData );
    }
    return defaultUserData;
  } );

  const [ isAuthenticated, setIsAuthenticated ] = useState( false );
  const location = useLocation();

  // Authentication logic
  useEffect( () => {
    const params = new URLSearchParams( location.search );
    const urlKey = params.get( 'key' );
    const storedAuth = localStorage.getItem( `${ clientId }_authenticated` );
    const expectedKey = landing.encodedKey;

    if ( urlKey === expectedKey && storedAuth === 'true' ) {
      setIsAuthenticated( true );
      localStorage.setItem( `${ clientId }_authenticated`, 'true' );
    } else {
      setIsAuthenticated( false );
    }
  }, [ location, clientId, landing.encodedKey ] );

  // Save userData to localStorage whenever it changes
  useEffect( () => {
    localStorage.setItem( 'userData', JSON.stringify( userData ) );
  }, [ userData, clientId ] );

  // Helper function to create the demographics object
  const createDemographicsObject = ( userData, finalHeight, finalWeight ) => {
    return {
      age: parseInt( userData.age ),
      birthDate: userData.dateOfBirth,
      gender: userData.genderId,
      height: finalHeight,
      weight: finalWeight,
      isFrontendHeightImperialUnits: userData.isFrontendHeightImperialUnits,
      isFrontendWeightImperialUnits: userData.isFrontendWeightImperialUnits,
      ethnicity: null,
      isSmoke: null,
      isDiabetes: null,
      hasHighBP: null,
      hasHadStroke: null,
      hasHadAttack: null,
      isOnBpMeds: null,
      isPregnant: null,
      hasMedicalCondition: null,
      medicalConditionNames: null,
      hasAllergies: null,
      allergyNames: null,
      hasSupplements: null,
      supplements: null,
      wellnessGoals: null,
    };
  };

  // Final submission handler for when all steps are completed
  const handleFinalSubmission = async () => {
    try {
      setIsLoading( true );
      setError( null );

      // Save BP value to localStorage with client prefix
      localStorage.setItem( `${ clientId }_regularBp`, userData.bloodPressure );

      // Handle height conversion and rounding
      const finalHeight = Math.round(
        userData.isFrontendHeightImperialUnits
          ? convertFeetAndInchesToCentimeters(
            Number( userData.height.ft ),
            Number( userData.height.in )
          )
          : Number( userData.height.cm )
      );

      // Handle weight conversion and rounding
      const finalWeight = Math.round(
        userData.isFrontendWeightImperialUnits
          ? convertPoundsToKilograms( Number( userData.weight ) )
          : Number( userData.weight )
      );

      const demographics = {
        age: parseInt( userData.age ),
        gender: userData.gender,
        height: finalHeight,
        weight: finalWeight,
      };

      // First Access API call
      const response = await clientApi.postDemographics( demographics );

      if ( response && response.data ) {
        let assessmentId = response.data.assessment_id;
        localStorage.setItem( `${ clientId }-assessment-id`, response.data.assessment_id );
        localStorage.setItem( `${ clientId }-session-id`, response.data.session_id );
        setOrgUser( response.data.session_id );
        localStorage.setItem( `${ clientId }-redirect-url`, response.data.redirect_url );

        // Prepare the demographics object for second API call
        const demographics2 = createDemographicsObject( userData, finalHeight, finalWeight );

        // Second API call for demographics
        try {
          const response2 = await clientApi.postDemographicsNew( demographics2, assessmentId );
          navigate( `/${ clientId }-pre-face-scan` );
        } catch ( error ) {
          setError( 'Failed to submit demographics. Please try again.' );
          console.error( 'Failed to post demographics:', error );
        }
      } else {
        throw new Error( 'Invalid API response' );
      }
    } catch ( err ) {
      console.error( 'Error submitting demographics:', err );
      setError( 'Failed to submit demographics. Please try again.' );
    } finally {
      setIsLoading( false );
    }
  };

  // Helper function to handle going to the next step
  const handleNextStep = () => {
    // If this is the last step, trigger the final submission
    if ( stepIndex === steps.length - 1 ) {
      handleFinalSubmission();
      return;
    }

    // Otherwise, just move to the next step
    setStepIndex( ( prev ) => prev + 1 );
  };

  // Show message if not authenticated
  if ( !isAuthenticated ) {
    return (
      <Box sx={ {
        width: '100%',
        height: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'white'
      } }>
        <Typography sx={ {
          fontSize: '20px',
          fontWeight: 500,
          textAlign: 'center',
          fontFamily: 'Poppins',
          padding: 3
        } }>
          Authorization required. Please use a valid authentication link to access this page.
        </Typography>
      </Box>
    );
  }

  if ( isLoading ) {
    return (
      <SimpleLoader
        loadingText="Loading..."
        textColor={ theme.primaryColor }
      />
    );
  }

  // Display error message if there was an API error
  if ( error ) {
    return (
      <Box sx={ {
        width: '100%',
        height: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'white',
        flexDirection: 'column',
        gap: 3
      } }>
        <Typography color="error" sx={ {
          fontSize: '18px',
          fontWeight: 500,
          textAlign: 'center',
          fontFamily: 'Poppins',
          padding: 3
        } }>
          { error }
        </Typography>
        <ClientButton
          text="Try Again"
          onClick={ () => setError( null ) }
          backgroundColor={ theme.primaryColor }
          sx={ {
            maxWidth: "320px",
            width: "100%"
          } }
        />
      </Box>
    );
  }

  const CurrentStep = steps[ stepIndex ];

  return (
    <ClientQuestionBase
      headerStep={ stepIndex + 1 }
      totalSteps={ steps.length }
      backButtonHandler={ () => setStepIndex( ( prev ) => Math.max( prev - 1, 0 ) ) }
      aimdLinearBarValue={ ( stepIndex + 1 ) * ( 100 / 10 ) }
      IsShowBackIcon={ stepIndex == 0 ? false : true }
    >
      <CurrentStep
        userData={ userData }
        setUserData={ setUserData }
        onNext={ handleNextStep }
        onBack={ () => setStepIndex( ( prev ) => Math.max( prev - 1, 0 ) ) }
      />
    </ClientQuestionBase>
  );
}